import { useState, useEffect, useMemo } from "react";

function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface MobileMenuState {
  isMobile: boolean;
  isOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
  handleKeyDown: (event: KeyboardEvent) => void;
}

interface MobileMenuConfig {
  breakpoint?: number;
  debounceDelay?: number;
}

export function useMobileMenu({
  breakpoint = 640,
  debounceDelay = 100,
}: MobileMenuConfig = {}): MobileMenuState {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkIfMobile = () => {
      const newIsMobile = window.innerWidth < breakpoint;
      setIsMobile(newIsMobile);
      if (!newIsMobile) {
        setIsOpen(false);
      }
    };

    const debouncedCheckIfMobile = debounce(checkIfMobile, debounceDelay);

    checkIfMobile();

    window.addEventListener("resize", debouncedCheckIfMobile);
    window.addEventListener("orientationchange", debouncedCheckIfMobile);

    return () => {
      window.removeEventListener("resize", debouncedCheckIfMobile);
      window.removeEventListener("orientationchange", debouncedCheckIfMobile);
    };
  }, [breakpoint, debounceDelay]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && isOpen) {
      closeMenu();
    }
  };

  return useMemo(
    () => ({
      isMobile,
      isOpen,
      toggleMenu,
      closeMenu,
      handleKeyDown,
    }),
    [isMobile, isOpen]
  );
}